import React, { useState, useEffect } from 'react';
import { ArrowUpIcon } from '@heroicons/react/24/outline';

const ScrollToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Show/hide button based on scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    
    // Check initial scroll position
    toggleVisibility();

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Smooth scroll to top function
  const scrollToTop = () => {
    if (isScrolling) return; // Prevent multiple simultaneous scrolls
    
    setIsScrolling(true);
    
    const duration = 1000; // Animation duration in milliseconds
    const start = window.pageYOffset;
    const startTime = performance.now();

    // Smooth easing function (ease-out cubic)
    const easeOutCubic = (t: number): number => {
      return 1 - Math.pow(1 - t, 3);
    };

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Apply smooth easing function
      const easedProgress = easeOutCubic(progress);
      
      // Calculate current position
      const currentPosition = start - (start * easedProgress);
      
      window.scrollTo(0, currentPosition);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        setIsScrolling(false);
      }
    };

    requestAnimationFrame(animateScroll);
  };
  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-2 scale-90 pointer-events-none'
      }`}
      style={{
        willChange: 'transform, opacity'
      }}
    >
      <button
        onClick={scrollToTop}
        disabled={isScrolling}
        className={`
          scroll-to-top-btn 
          bg-primary-600 
          text-white 
          rounded-full 
          p-3 
          shadow-lg 
          hover:bg-primary-700 
          hover:shadow-xl 
          transform 
          transition-all 
          duration-200 
          ease-out
          hover:-translate-y-1 
          hover:scale-105 
          active:translate-y-0 
          active:scale-95
          focus:outline-none 
          focus:ring-2 
          focus:ring-primary-500 
          focus:ring-offset-2
          disabled:opacity-75
          disabled:cursor-not-allowed
          flex
          items-center
          justify-center
        `}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <ArrowUpIcon className={`h-6 w-6 transition-transform duration-200 ${isScrolling ? 'animate-pulse' : ''}`} />
      </button>
    </div>
  );
};

export default ScrollToTopButton;
